import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import StarIcon from '@mui/icons-material/Star';
import { LoadingSpinner } from '../../core/LoadingSpinner';
import { useAlert } from '../../context/AlertContext';
import { useConfirmation } from '../../context/ConfirmationContext';
import { useNavigate } from 'react-router-dom';
import {
  useDeleteRatingForm,
  useGetOpenFormulareStatus,
  useGetRatingForms,
} from '../../hooks/reactQuerys/RatingFormQueries';
import { IRatingForm } from '../../Interfaces/IRatingForm';
import { RatingFormEditDialog } from './RatingFormEditDialog';

export const RatingFormOverview: React.FC = () => {
  const { ratingForms, isLoadingRatingForms, refetchRatingForms } =
    useGetRatingForms();
  const { deleteRatingForm } = useDeleteRatingForm();
  const { ofStatus } = useGetOpenFormulareStatus();
  const { showAlert } = useAlert();
  const { showConfirmation } = useConfirmation();
  const navigate = useNavigate();
  const [editing, setEditing] = useState<IRatingForm | 'new' | null>(null);

  const sorted = useMemo(() => {
    if (!ratingForms) return [];
    return [...ratingForms].sort((a, b) => {
      if ((a.IsDefault ? 1 : 0) !== (b.IsDefault ? 1 : 0)) {
        return a.IsDefault ? -1 : 1;
      }
      return (a.Title || '').localeCompare(b.Title || '');
    });
  }, [ratingForms]);

  if (isLoadingRatingForms) return <LoadingSpinner />;

  const handleDelete = async (form: IRatingForm) => {
    if (!form.idRatingForm) return;
    if (form.IsDefault) {
      showAlert({
        text: 'Standard-Bewertungsbogen kann nicht gelöscht werden.',
        severity: 'warning',
      });
      return;
    }
    const ok = await showConfirmation({
      title: 'Bewertungsbogen löschen?',
      message: `„${form.Title}" wird endgültig gelöscht.`,
      confirmButtonText: 'Löschen',
      abortButtonText: 'Abbrechen',
    });
    if (!ok) return;
    try {
      await deleteRatingForm(form.idRatingForm);
      await refetchRatingForms();
      showAlert({ text: 'Bewertungsbogen gelöscht.', severity: 'success' });
    } catch (err) {
      console.error(err);
      showAlert({ text: 'Löschen fehlgeschlagen.', severity: 'error' });
    }
  };

  return (
    <Box>
      <Stack
        direction='row'
        alignItems='baseline'
        justifyContent='space-between'
        sx={{ mb: 2 }}
      >
        <Typography variant='h4'>Bewertungsbögen</Typography>
        <Button
          startIcon={<AddIcon />}
          variant='contained'
          onClick={() => setEditing('new')}
        >
          Neuen Bogen anlegen
        </Button>
      </Stack>

      <Typography variant='body2' color='text.secondary' sx={{ mb: 3 }}>
        Hier verwalten Sie die verfügbaren Bewertungsbögen. Genau ein Bogen ist
        der <strong>Standard-Bogen</strong> — er ist in jedem Vorgang
        standardmäßig vorausgewählt. Im Vorgang selbst (Tab{' '}
        <strong>„Bewertungsbögen"</strong>) kann pro Vorgang ein anderer Bogen
        gewählt und der Versandzeitpunkt festgelegt werden (standardmäßig beim
        Abschluss des Vorgangs). Eine Zuordnung zur Rechtsgeschäftsart gibt es
        nicht mehr.
      </Typography>

      {ofStatus && !ofStatus.isReady && (
        <Alert
          severity='warning'
          sx={{ mb: 3 }}
          action={
            <Button
              color='inherit'
              size='small'
              onClick={() => navigate('/admin/apps')}
            >
              Zu den Erweiterungen
            </Button>
          }
        >
          Um Bewertungsbögen versenden zu können, muss die Erweiterung
          „OpenFormulare" aktiviert und konfiguriert sein.{' '}
          {!ofStatus.isActive
            ? 'Sie ist derzeit nicht aktiviert.'
            : `Es fehlen noch folgende Einstellungen: ${ofStatus.missing.join(', ')}.`}
        </Alert>
      )}

      {!sorted.length ? (
        <Alert severity='info'>
          Es ist noch kein Bewertungsbogen angelegt. Beginnen Sie mit „Neuen
          Bogen anlegen".
        </Alert>
      ) : (
        <TableContainer>
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell>Titel</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align='center'>Anzahl Fragen</TableCell>
                <Tooltip title='Ab diesem Gesamtwert (in Sternen) wird dem Mandanten nach Abgabe der Bewertungs-Link angezeigt.'>
                  <TableCell>Schwellenwert für Bewertungs-Link</TableCell>
                </Tooltip>
                <TableCell width={120}>Aktion</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sorted.map(form => (
                <TableRow key={form.idRatingForm}>
                  <TableCell>
                    <Stack direction='row' spacing={1} alignItems='center'>
                      {form.IsDefault ? (
                        <Tooltip title='Standard-Bewertungsbogen'>
                          <StarIcon fontSize='small' color='primary' />
                        </Tooltip>
                      ) : null}
                      <Box>
                        <Typography>{form.Title}</Typography>
                      </Box>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Stack direction='row' spacing={0.5} alignItems='center'>
                      <span>{form.IsActive ? 'Aktiv' : 'Inaktiv'}</span>
                      {form.IsDefault && (
                        <Chip
                          size='small'
                          color='primary'
                          variant='outlined'
                          label='Standard'
                        />
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell align='center'>
                    {form.QuestionCount ?? 0}
                  </TableCell>
                  <TableCell>
                    {form.ThresholdScore != null ? (
                      <Stack direction='row' spacing={0.5} alignItems='center'>
                        <span>≥ {form.ThresholdScore}</span>
                        <StarIcon fontSize='small' color='warning' />
                      </Stack>
                    ) : (
                      '–'
                    )}
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size='small'
                      onClick={() => setEditing(form)}
                      aria-label='Bearbeiten'
                    >
                      <EditIcon fontSize='small' />
                    </IconButton>
                    <IconButton
                      size='small'
                      onClick={() => handleDelete(form)}
                      aria-label='Löschen'
                      disabled={!!form.IsDefault}
                    >
                      <DeleteIcon fontSize='small' />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {editing && (
        <RatingFormEditDialog
          form={editing === 'new' ? null : editing}
          open
          onClose={() => setEditing(null)}
          onSaved={async () => {
            await refetchRatingForms();
            setEditing(null);
          }}
        />
      )}
    </Box>
  );
};
